import CloseIcon from '@mui/icons-material/Close';
import { Box, Divider, IconButton, Modal, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import React, { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { EAttributeType } from '../../@types/AttributeTypes';

import AttributeModel, { IAttribute } from '../../models/standard/AttributeModel';
import { useLoginContext } from '../../provider/LoginProvider';
import { usePlan } from '../../provider/PlanProvider';
import { AttributeSelectionInput, mergeAttributesById, resolveAttributeSelection } from '../../utils/attributes/attributeCreationUtils';
import { getLocalizedAttributeLabel } from '../../utils/attributes/attributeUtils'; // Added Import
import filesApiService from '../../utils/filesApiService';
import AttributeAutocomplete from '../common/AttributeAutocomplete';

const useStyles = makeStyles((theme) => ({
  modalBox: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 750,
    height: 220,
    backgroundColor: theme.palette.background.paper,
    borderRadius: 15,
    boxShadow: theme.shadows[5],
    padding: theme.spacing(1, 2),
    paddingTop: theme.spacing(0.5),
    display: 'flex',
    flexDirection: 'column',
    overflow: 'visible',
    border: 'none',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  modalTitle: {
    fontSize: '1.5rem !important',
    fontWeight: 500,
  },
  divider: {
    marginLeft: '-16px !important',
    marginRight: '-16px !important',
    backgroundColor: '#d3d3d3',
    height: '1px',
  },
  footer: {
    marginTop: 'auto',
    display: 'flex',
    justifyContent: 'flex-end',
  },
  closeIcon: {
    fontSize: '1.6rem !important',
  },
}));

const AttachButton = styled('button')(({ theme }: any) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: 22,
  fontSize: '1rem',
  padding: theme.spacing(2, 4),
  marginBottom: theme.spacing(1),
  textTransform: 'none',
  cursor: 'pointer',
  backgroundColor: '#009fe3',
  color: '#ffffff',
  '&:hover': { backgroundColor: '#009fe3' },
  '&.Mui-disabled, &[disabled]': {
    backgroundColor: 'lightgrey',
    color: 'white',
    cursor: 'default',
  },
}));

interface AttachmentAttributeModalInterface {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (attribute: AttributeModel) => void;
}

const AttachmentAttributeModal: React.FC<AttachmentAttributeModalInterface> = ({ isOpen, onClose, onSelect }) => {
  const classes = useStyles();
  const { formatMessage, locale } = useIntl(); // Get locale
  const { isAuthenticated } = useLoginContext();

  const [attributes, setAttributes] = useState<IAttribute[]>([]);
  const [selected, setSelected] = useState<IAttribute | null>(null);
  const [inputValue, setInputValue] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const { planModel } = usePlan();
  const regionCode = planModel?.region ? String(planModel.region).toUpperCase() : 'DE';

  const refreshAttributes = useCallback(
    async (createdAttribute?: IAttribute): Promise<void> => {
      const fetchedAttributes = await filesApiService.getAttributes(regionCode);
      setAttributes((prev) => (createdAttribute ? mergeAttributesById(fetchedAttributes, prev, [createdAttribute]) : fetchedAttributes));
      setError(null);
    },
    [regionCode],
  );

  useEffect(() => {
    if (!isOpen || !isAuthenticated) return;
    refreshAttributes().catch((e) => {
      const msg = e instanceof Error ? e.message : 'Unknown error';
      setError(`Error fetching attributes: ${msg}`);
    });
  }, [isOpen, isAuthenticated, refreshAttributes]);

  const handleAttributeSelection = useCallback(
    async (value: AttributeSelectionInput) => {
      setError(null);

      try {
        const resolvedAttribute = await resolveAttributeSelection({
          attributeCatalog: attributes,
          currentAttributes: [],
          locale,
          onAttributeCreated: (createdAttribute) => {
            setAttributes((prev) => mergeAttributesById(prev, [createdAttribute]));
            refreshAttributes(createdAttribute).catch(() => undefined);
          },
          selection: value,
        });

        setSelected(resolvedAttribute);
        setInputValue(resolvedAttribute ? getLocalizedAttributeLabel(resolvedAttribute, locale) : '');
      } catch {
        setError(
          formatMessage({
            id: 'AttributeAutocomplete.createAttributeError',
            defaultMessage: 'Attribute could not be created. Please make sure you are logged in.',
          }),
        );
      }
    },
    [attributes, formatMessage, locale, refreshAttributes],
  );

  const close = () => {
    setSelected(null);
    setInputValue('');
    setError(null);
    onClose();
  };

  const attach = () => {
    if (!selected) return;
    
    const attrModel = new AttributeModel(
      selected.id,
      selected.key,
      undefined, 
      null,
      selected.type as EAttributeType,
      selected.unit,
      selected.hidden,
      selected.forced,
      selected.ownerId,
      selected.userId,
      selected.publicFlag,
      selected.localization,
      selected.defaults,
      selected.validation,
      selected.unitFamily
    );

    onSelect(attrModel);
    close();
  };

  return isAuthenticated ? (
    <Modal open={isOpen} onClose={close} aria-labelledby="attachment-modal-title" BackdropProps={{ style: { backgroundColor: 'rgba(0,0,0,0.5)' } }}>
      <Box className={classes.modalBox} role="dialog">
        <div className={classes.modalHeader}>
          <Typography id="attachment-modal-title" className={classes.modalTitle} style={{ color: '#464646' }}>
            {formatMessage({
              id: 'AttachmentModal.title',
              defaultMessage: 'Attribut hinzufügen',
            })}
          </Typography>
          <IconButton onClick={close}>
            <CloseIcon className={classes.closeIcon} style={{ color: '#464646' }} />
          </IconButton>
        </div>

        <Divider className={classes.divider} />

        <AttributeAutocomplete
          attributes={attributes}
          selected={selected}
          inputValue={inputValue}
          error={error}
          onChange={handleAttributeSelection}
          onInputChange={(val) => {
            setInputValue(val);
            const currentLabel = selected ? getLocalizedAttributeLabel(selected, locale) : '';
            if (val !== currentLabel) setSelected(null);
          }}
          width="70%"
        />

        <Box className={classes.footer}>
          <AttachButton disabled={!selected || !!error} onClick={attach} sx={{ textTransform: 'uppercase' }}>
            {formatMessage({ id: 'AttachmentModal.attachButton', defaultMessage: 'HINZUFÜGEN' })}
          </AttachButton>
        </Box>
      </Box>
    </Modal>
  ) : null;
};

export default AttachmentAttributeModal;
